import { solveExpressionProperty } from '@/helpers/expressions'

/**
 * Seleciona o primeiro estado dentro os indicados que não esteja oculto (hidden).
 * Note que o estado invisivel (invisible) não é considerado oculto e portanto, se for o primeiro
 * não oculto, será retornado.
 * @param {*} states Os estados analisados. Por exemplo, estados (states) de um link.
 * @param {*} context O contexto para resolver as expressões.
 * @returns O primeiro estado não oculto ou indefinido se todos estiverem ocultos.
 */
export function selectState(states, context) {
  return states?.find(state => {
    const options = { hidden: false }
    // Resolvendo as expressoes.
    if (state.expression) {
      solveExpressionProperty(state, options, context)
    }
    return !options.hidden
  })
}

/**
 * Função para captura das propriedades de estilo de um elemento, resolvendo as expressões
 * e retornando um objeto com as propriedades resolvidas.
 *
 * @param {Array} elements - Um array de objetos quem contêm as propriedades de estilo.
 * @param {Object} context - O contexto para resolver as expressões.
 *    @property {Object} page - A entidade de contexto da página.
 *    @property {Object} data - O objeto de contexto dos dados.
 *    @property {Object} row - O objeto de contexto da linha.
 * @returns {Object}
 *    Um objeto com as propriedades de estilo resolvidas.
 *    @property {string} title - A propriedade `title` resolvida.
 *    @property {string} subtitle - A propriedade `subtitle` resolvida.
 *    @property {string} description - A propriedade `description` resolvida.
 *    @property {string} gerund - A propriedade `gerund` resolvida.
 *    @property {string} verb - A propriedade `verb` resolvida.
 *    @property {string} icon - A propriedade `icon` resolvida.
 *    @property {boolean} disabled - A propriedade `disabled` resolvida.
 *    @property {boolean} hidden - A propriedade `hidden` resolvida.
 *    @property {boolean} invisible - A propriedade `invisible` resolvida.
 *    @property {string} icon - A propriedade `icon` resolvida.
 *    @property {string} color - A propriedade `color` resolvida.
 *    @property {string} style - A propriedade `style` resolvida.
 *    @property {string} font - A propriedade `font` resolvida.
 */
export function selectOptions(elements, context) {
  // Se elementos não é vetor, converter para vetor.
  if (!Array.isArray(elements)) elements = [elements]
  elements = elements.filter(e => e)
  const reversed = elements.reverse()
  // Checamos a ordem inversa porque queremos que o último elemento tenha prioridade.
  const options = {
    disabled: reversed.find(e => e.disabled)?.disabled,
    hidden: reversed.find(e => e.hidden)?.hidden,
    invisible: reversed.find(e => e.invisible)?.invisible,
    title: reversed.find(e => e.title)?.title,
    subtitle: reversed.find(e => e.subtitle)?.subtitle,
    description: reversed.find(e => e.description)?.description,
    gerund: reversed.find(e => e.gerund)?.gerund,
    verb: reversed.find(e => e.verb)?.verb,
    icon: reversed.find(e => e.icon)?.icon,
    color: reversed.find(e => e.color)?.color,
    style: reversed.find(e => e.style)?.style,
    font: reversed.find(e => e.font)?.font,
    // align
    // matiz
  }

  // Resolvendo as expressoes.
  // Usamos a ordem natural para que a última expressão tenha prioridade.
  const solvableElements = elements.filter(e => e.expression)
  solvableElements.forEach(e => solveExpressionProperty(e, options, context))

  return options
}

export function selectClass(style) {
  const value = {
    1: "error--text", // critical
    2: "error--text", // error
    3: "warning--text", // warning
    4: "success--text", // success
    5: "accent--text", // highlight
    6: "primary--text", // information
    7: "", // default
    8: "secondary--text text--grayed-2", // trace
    Critical: "error--text",
    Error: "error--text",
    Warning: "warning--text",
    Success: "success--text",
    Highlight: "accent--text",
    Information: "primary--text",
    Default: "",
    Trace: "secondary--text text--grayed-2",
    // extras
    Accent: "accent--text",
    Primary: "primary--text",
    Secondary: "secondary--text",
    Trivial: "secondary--text text--grayed-2",
  }[style]
  if (value) return value
  if (style) return `${style}--text`;
  return style
}

export function selectColor(style) {
  return {
    1: "error", // critical
    2: "error", // error
    3: "warning", // warning
    4: "success", // success
    5: "accent", // highlight
    6: "primary", // information
    7: "", // default
    8: "secondary grayed-2", // trace
    Critical: "error",
    Error: "error",
    Warning: "warning",
    Success: "success",
    Highlight: "accent",
    Information: "primary",
    Default: "",
    Trace: "secondary grayed-2",
    // extras
    Accent: "accent",
    Primary: "primary",
    Secondary: "secondary",
    Trivial: "secondary grayed-2",
  }[style] || style?.toLowerCase();
}

// Funções para detecção de estilo de fonte

export const DEFAULT = 0
export const ITALIC = 1
export const BOLD = 2
export const OVERLINE = 4
export const STRIKETHROUGH = 8
export const UNDERLINE = 16
export const MONOSPACE = 32

export function hasFontStyle(font, style) { return hasFlag(getFlagFromFontStyle(font), style); }
export function isItalic(font) { return hasFontStyle(font, ITALIC) }
export function isBold(font) { return hasFontStyle(font, BOLD) }
export function isOverline(font) { return hasFontStyle(font, OVERLINE) }
export function isStrikethrough(font) { return hasFontStyle(font, STRIKETHROUGH) }
export function isUnderline(font) { return hasFontStyle(font, UNDERLINE) }
export function isMonospace(font) { return hasFontStyle(font, MONOSPACE) }

/**
 * Gera uma string de valor para a propriedade `font` do CSS com base nas características
 * da fonte fornecida.
 * Esta função ajusta condicionalmente o estilo (italic ou normal), o peso (bold ou normal)
 * da fonte, e inclui a família da fonte (monospace) apenas se especificado. A função é
 * projetada para permitir a customização de aspectos específicos da fonte sem definir o
 * tamanho ou a família da fonte por padrão, oferecendo flexibilidade para definir esses
 * valores separadamente no CSS.
 *
 * @param {Object} font - Um objeto representando as características da fonte a serem aplicadas.
 * @returns {string}
 *    Uma string representando o valor da propriedade `font` para uso no CSS.
 *    A string pode incluir `font-style`, `font-weight`, e `font-family` (se monospace),
 *    conforme aplicável, mas omite `font-size` e `font-family` (a menos que seja monospace).
 *
 * Exemplo de uso:
 *   let fontStyles = selectFont({
 *     isItalic: true,
 *     isBold: false,
 *     isMonospace: true
 *   });
 *   // Retorna "italic normal monospace"
 */
export function selectFont(font) {
  if (!font) return {};

  const fontStyle = isItalic(font) ? 'italic' : undefined;
  const fontWeight = isBold(font) ? 'bold' : undefined;
  const fontFamily = isMonospace(font) ? 'monospace' : undefined;

  // Inicializa um array para coletar valores de text-decoration.
  let textDecorationStyles = [];
  if (isOverline(font)) textDecorationStyles.push('overline');
  if (isStrikethrough(font)) textDecorationStyles.push('line-through');
  if (isUnderline(font)) textDecorationStyles.push('underline');

  // Junta os valores com espaço, se houver algum estilo de texto.
  const textDecoration = textDecorationStyles.length > 0 ? textDecorationStyles.join(' ') : undefined;

  return { fontStyle, fontWeight, fontFamily, textDecoration };
}

/**
 * Verifica se a flag está presente no valor.
 *
 * @param {number} value
 *    O valor inteiro que pode conter a flag.
 *    Nulo ou indefinido são considerados como 0.
 * @param {number} flag
 *    A flag a ser testada.
 * @returns {boolean}
 *    Verdadeiro se a flag estiver presente no valor.
 */
export function hasFlag(value, flag) {
  return (value || 0) & flag;
}

/**
 * Obtém o valor inteiro correspondente ao estilo de fonte fornecido.
 *
 * Esta função aceita tanto estilos de fonte em formato string quanto valores numéricos diretamente.
 * No caso de uma string, os estilos podem ser combinados, separados por vírgulas.
 *
 * Correspondência de valores:
 * - Default = 0
 * - Italic = 1
 * - Bold = 2
 * - Overline = 4
 * - Strikethrough = 8
 * - Underline = 16
 * - Monospace = 32
 *
 * @param {string|number} fontStyle - O estilo de fonte a ser convertido.
 *    Se for uma string:
 *      - A comparação é case-insensitive.
 *      - Se nulo, vazio ou não reconhecido, é tratado como "Default".
 *    Se for um número:
 *      - O próprio valor é retornado.
 * @returns {number} O valor inteiro correspondente ao estilo de fonte.
 */
export function getFlagFromFontStyle(fontStyle) {
  const styleFlags = {
    default: 0,
    italic: 1,
    bold: 2,
    overline: 4,
    strikethrough: 8,
    underline: 16,
    monospace: 32
  };

  if (typeof fontStyle === "number") {
    return fontStyle;
  }

  if (typeof fontStyle === "string") {
    // Dividir a string pelos estilos
    const styles = fontStyle.split(',').map(s => s.trim().toLowerCase());

    // Calcular a flag total para os estilos
    return styles.reduce((acc, style) => {
      return acc + (styleFlags[style] || 0);
    }, 0);
  }

  return 0;
}
